import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit3, Check, X, User, Mail, Phone, Calendar, Clock, Upload } from 'lucide-react';
import api from '../../../../api/client';
import { PhoneInput, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';

/** A "headless" phone input that manages state but renders a plain HTML input without a flag. */
const HeadlessPhoneInput = ({ value, onChange, onKeyPress, className, autoFocus }) => {
    const { inputValue, handlePhoneValueChange, inputRef } = usePhoneInput({
        defaultCountry: 'in',
        value: value,
        onChange: (data) => onChange(data.phone),
    });

    return (
        <input
            ref={inputRef}
            value={inputValue}
            onChange={handlePhoneValueChange}
            onKeyPress={onKeyPress}
            type="tel"
            className={className}
            autoFocus={autoFocus}
        />
    );
};

const CandidateInfoCards = ({
    candidateData,
    setCandidateData,
    editingField,
    setEditingField,
    editValue,
    setEditValue,
    isSaving,
    setIsSaving,
    setSnackbar,
    id,
    onUpdateResume // Added onUpdateResume prop
}) => {
    const { t } = useTranslation();
    const [phoneError, setPhoneError] = React.useState('');

    const startEditing = (field, currentValue) => {
        setEditingField(field);
        setEditValue(currentValue || '');
        setPhoneError('');
    };

    const cancelEditing = () => {
        setEditingField(null);
        setEditValue('');
        setPhoneError('');
    };

    const saveEdit = async () => {
        if (!editingField || !editValue.trim()) {
            cancelEditing();
            return;
        }

        setIsSaving(true);
        try {
            let updateData = {};

            switch (editingField) {
                case 'firstName':
                    updateData = { first_name: editValue.trim() };
                    break;
                case 'lastName':
                    updateData = { last_name: editValue.trim() };
                    break;
                case 'email':
                    updateData = { email: editValue.trim() };
                    break;
                case 'mobile':
                    // Clean and attempt to parse
                    let cleanedPhone = editValue.trim().replace(/\s/g, '');
                    let p = parsePhoneNumberFromString(cleanedPhone);

                    // If invalid, try to handle common international numbering issues 
                    // (like redundant leading 0 after country code, e.g. +33 06...)
                    if (!p || !p.isValid()) {
                        // Regex to check for +[countryCode]0[restOfNumber]
                        // We try to strip the '0' if it's clearly a redundant trunk prefix
                        const possibleMatch = cleanedPhone.match(/^(\+\d+)(0)(\d+)$/);
                        if (possibleMatch) {
                            const retryValue = possibleMatch[1] + possibleMatch[3];
                            const pRetry = parsePhoneNumberFromString(retryValue);
                            if (pRetry && pRetry.isValid()) {
                                p = pRetry;
                            }
                        }
                    }

                    // Final check after potential auto-fix
                    if (!p || !p.isValid()) {
                        setPhoneError(t('bulkUpload.invalidPhone', 'Invalid phone number'));
                        setIsSaving(false);
                        return;
                    }

                    updateData = {
                        mobile: p.nationalNumber,
                        country_code: `+${p.countryCallingCode}`
                    };
                    break;
                default:
                    throw new Error('Invalid field');
            }

            await api.patch(`candidates/${id}/`, updateData);

            setCandidateData(prev => ({
                ...prev,
                ...updateData,
                profile: {
                    ...prev.profile,
                    ...updateData
                }
            }));

            setSnackbar({
                open: true,
                message: t('candidateDetails.detailsUpdatedSuccess'),
                severity: 'success'
            });

            cancelEditing();
        } catch (err) {
            console.error('Error updating candidate:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.error || t('candidateDetails.detailsUpdateFailed'),
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveEdit();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleString();
    };

    const infoCards = [
        {
            field: 'firstName',
            label: t('candidateDetails.firstName'),
            icon: User,
            value: candidateData?.first_name,
            editable: true
        },
        {
            field: 'lastName',
            label: t('candidateDetails.lastName'),
            icon: User,
            value: candidateData?.last_name,
            editable: true
        },
        {
            field: 'email',
            label: t('candidateDetails.email'),
            icon: Mail,
            value: candidateData?.email,
            editable: true
        },
        {
            field: 'mobile',
            label: t('candidateDetails.phone'),
            icon: Phone,
            value: candidateData?.profile?.country_code && candidateData?.profile?.mobile
                ? `${candidateData.profile.country_code} ${candidateData.profile.mobile}`
                : null,
            editable: true,
            editValue: candidateData?.profile?.country_code && candidateData?.profile?.mobile
                ? `${candidateData.profile.country_code}${candidateData.profile.mobile}`
                : candidateData?.profile?.mobile
        },
        {
            field: 'dateJoined',
            label: t('candidateDetails.dateJoined'),
            icon: Calendar,
            value: candidateData?.date_joined ? formatDate(candidateData.date_joined) : null,
            editable: false
        },
        {
            field: 'resumeUpdatedAt',
            label: t('candidateDetails.resumeUpdatedAt'),
            icon: Clock,
            value: candidateData?.updated_at ? formatDateTime(candidateData.updated_at) : null,
            editable: false
        },
        {
            field: 'status',
            label: t('candidateDetails.status'),
            icon: User,
            value: t('candidateDetails.active'),
            editable: false
        }
    ];

    return (
        <div className="space-y-4">
            {/* Compact Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {infoCards.map((card) => {
                    const IconComponent = card.icon;
                    const isEditing = editingField === card.field;

                    return (
                        <div
                            key={card.field}
                            className="bg-gradient-to-br from-white to-gray-50/50 rounded-xl border border-gray-200/50 p-4 hover:shadow-md hover:border-blue-200/50 transition-all duration-200 group"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg group-hover:from-blue-100 group-hover:to-purple-100 transition-colors duration-200">
                                    <IconComponent size={16} className="text-blue-600" />
                                </div>
                                <h4 className="text-sm font-medium text-gray-700 truncate">
                                    {card.label}
                                </h4>
                            </div>

                            {isEditing ? (
                                <div className="relative">
                                    {card.field === 'mobile' ? (
                                        <div className="flex flex-col gap-1 w-full">
                                            <HeadlessPhoneInput
                                                value={editValue}
                                                onChange={(phone) => {
                                                    setEditValue(phone);
                                                    if (phoneError) setPhoneError('');
                                                }}
                                                onKeyPress={handleKeyPress}
                                                autoFocus
                                                className="!w-full !px-3 !py-2 !pr-16 !border !border-gray-500 !rounded !text-black !font-normal !focus:ring-2 !focus:ring-blue-500 !focus:border-blue-500 !text-sm"
                                            />
                                            {phoneError && (
                                                <span className="text-[10px] text-red-500 font-medium leading-none">
                                                    {phoneError}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            autoFocus
                                            className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-200 text-sm"
                                            placeholder={t('candidateDetails.enterField', { field: card.label })}
                                        />
                                    )}
                                    <div className="absolute right-2 top-2 flex items-center gap-1">
                                        <button
                                            onClick={saveEdit}
                                            disabled={isSaving}
                                            className="p-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-md transition-colors disabled:opacity-50"
                                        >
                                            <Check size={12} />
                                        </button>
                                        <button
                                            onClick={cancelEditing}
                                            className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`${card.editable
                                            ? 'cursor-pointer hover:bg-blue-50/50 rounded-lg p-2 -m-2 transition-all duration-200 group/edit'
                                            : ''
                                        }`}
                                    onClick={card.editable ? () => startEditing(card.field, card.editValue || card.value) : undefined}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-900 text-sm font-medium truncate">
                                            {card.value || (
                                                <span className="text-gray-400 italic">{t('candidateDetails.nA')}</span>
                                            )}
                                        </span>
                                        {card.editable && (
                                            <Edit3 size={12} className="text-gray-400 group-hover/edit:text-blue-600 opacity-0 group-hover/edit:opacity-100 transition-all duration-200 flex-shrink-0 ml-2" />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Update Resume Button */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/50 p-4 hover:shadow-md transition-all duration-200">
                <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
                        <Upload size={16} className="text-blue-600" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-700">
                        {t('candidateDetails.updateResume')}
                    </h4>
                </div>
                <button
                    onClick={onUpdateResume}
                    className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                >
                    <Upload size={14} />
                    {t('candidateDetails.update')}
                </button>
            </div>
        </div>
    );
};

export default CandidateInfoCards;